// ---------------------------------------------------------------------------
// Proof-of-work bot check — client side.
//
// Before each anonymous AI call (/try, /spark) the browser must present a
// freshly solved proof of work. Solving runs in a Web Worker (see powWorker.ts)
// so it never blocks the UI, and this module keeps a small pool of pre-solved
// proofs warm so the visitor never actually waits: by the time they send a
// message, a proof is usually already sitting ready.
//
// This is FUNCTIONAL / security infrastructure, not analytics — it loads
// unconditionally, NOT behind the marketing-consent toggle. It carries no idea
// content; a proof is just "rand.ts.nonce". The backend re-verifies every proof
// (common/pow.ts) and refuses missing, stale, under-difficulty, or replayed
// ones. No third party, no account, no keys.
// ---------------------------------------------------------------------------

import { BOT_CHECK_PREFIX, POW_FRESHNESS_MS, POW_DIFFICULTY_BITS } from '../../../../methods/src/common/powShared';
import { solveChunked } from './powSolve';
import { analytics } from '@mindstudio-ai/interface';
import PowWorker from './powWorker?worker';

/** How many solved proofs to keep ready. Each is single-use (replay-guarded). */
const POOL_TARGET = 2;

/**
 * A proof embeds the timestamp it was solved at, and the backend refuses any
 * proof older than POW_FRESHNESS_MS. A pooled proof is only usable well within
 * that window — we keep a full minute of slack for the network round-trip and
 * any backend clock skew. When a visitor reads/lingers on the page past this,
 * the pre-solved pool would otherwise go stale and BOTH the primary proof and
 * the retry proof would be rejected, surfacing "could not verify your browser".
 */
const PROOF_MAX_AGE_MS = Math.max(45_000, POW_FRESHNESS_MS - 60_000);

/** Age (ms) of a "rand.ts.nonce" token from its embedded solve timestamp. */
function proofAge(token: string): number {
  const ts = Number(token.split('.')[1]);
  return Number.isFinite(ts) ? Date.now() - ts : Number.POSITIVE_INFINITY;
}
const isFresh = (token: string): boolean => proofAge(token) < PROOF_MAX_AGE_MS;

let worker: Worker | null = null;
let seq = 0;
const waiters = new Map<number, { resolve: (t: string) => void; reject: (e: Error) => void }>();
const ready: string[] = [];
let solving = 0;

function ensureWorker(): Worker | null {
  if (worker) return worker;
  if (typeof window === 'undefined' || typeof Worker === 'undefined') return null;
  try {
    // Imported with `?worker` so Vite compiles it into its own JS chunk. (A bare
    // `new URL('./powWorker.ts', import.meta.url)` shipped the raw TypeScript as
    // a data URL, which never ran, so every visitor fell back to the main thread.)
    // Assets are served from the site's own origin, so no cross-origin shim is needed.
    worker = new PowWorker();
    worker.onmessage = (e: MessageEvent) => {
      const { id, token, error } = (e.data || {}) as { id?: number; token?: string; error?: string };
      if (id == null) return;
      const wtr = waiters.get(id);
      if (!wtr) return;
      waiters.delete(id);
      if (token) wtr.resolve(token);
      else wtr.reject(new Error(error || 'proof failed'));
    };
    worker.onerror = () => {
      // Worker died — fail every pending solve so callers fall back cleanly.
      for (const [, wtr] of waiters) wtr.reject(new Error('proof worker error'));
      waiters.clear();
    };
  } catch {
    worker = null;
  }
  return worker;
}

/**
 * One freshly solved proof: from the worker, or, when the worker is missing,
 * crashes, or stalls (some phones suspend workers while the visitor switches
 * apps), on the main thread instead. The fallback has no timeout, so a page that
 * was briefly in the background simply finishes when the visitor returns.
 */
let workerBroken = false;
async function solveOne(): Promise<string> {
  if (!workerBroken) {
    try {
      return await solveInWorker();
    } catch (err) {
      workerBroken = true;
      try {
        // Fixed codes only, never raw error text (analytics must not carry message strings).
        const m = err instanceof Error ? err.message : '';
        const reason = m === 'proof worker unavailable' ? 'unavailable' : m === 'proof timed out' ? 'timeout' : 'error';
        analytics.track('bot_check_fallback', { reason });
      } catch {
        /* analytics is best-effort */
      }
    }
  }
  return solveChunked(POW_DIFFICULTY_BITS);
}

/** Ask the worker for one freshly solved proof. */
function solveInWorker(timeoutMs = 15000): Promise<string> {
  const wk = ensureWorker();
  if (!wk) return Promise.reject(new Error('proof worker unavailable'));
  const id = ++seq;
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      if (waiters.has(id)) {
        waiters.delete(id);
        reject(new Error('proof timed out'));
      }
    }, timeoutMs);
    waiters.set(id, {
      resolve: (t) => {
        clearTimeout(timer);
        resolve(t);
      },
      reject: (e) => {
        clearTimeout(timer);
        reject(e);
      },
    });
    wk.postMessage({ id });
  });
}

/** Keep the pool of ready proofs topped up (fire-and-forget). */
function topUp(): void {
  while (ready.length + solving < POOL_TARGET) {
    solving++;
    solveOne()
      .then((t) => {
        ready.push(t);
      })
      .catch(() => {
        /* transient — a later getProof will try again */
      })
      .finally(() => {
        solving--;
      });
  }
}

/**
 * Warm the worker + proof pool ahead of first use. Best-effort; safe to call on
 * page mount. Call it once the /try or /spark page loads.
 */
export function initBotCheck(): void {
  topUp();
}

/** Take a ready proof if there is one, else solve on demand. Refills the pool. */
async function getProof(): Promise<string> {
  // Drop any pooled proofs that went stale while the visitor lingered, so we
  // never hand the backend an expired proof it will reject.
  while (ready.length && !isFresh(ready[0])) ready.shift();
  if (ready.length) {
    const token = ready.shift() as string;
    topUp();
    return token;
  }
  const pending = solveOne();
  topUp();
  return pending;
}

/**
 * Shown when a real visitor genuinely can't produce a proof (no Worker support,
 * or a proof rejected twice). Rare — the pool almost always has one ready.
 */
export const BOT_CHECK_USER_MESSAGE =
  'We could not verify your browser. Refresh the page and try again.';

/**
 * Run an anonymous AI request behind a fresh proof of work. Solves (or takes a
 * pooled) proof, calls `call(token)`, and if the backend rejects the proof
 * (stale or replayed) solves a GENUINELY FRESH one and retries exactly once —
 * the retry never reuses the pool, since the whole pool may have gone stale
 * together. A failure to produce a proof, or a second rejection, surfaces as
 * `BOT_CHECK_USER_MESSAGE`; unrelated errors from `call` bubble up unchanged.
 */
function failed(reason: 'solve' | 'retry' | 'rejected'): Error {
  try {
    analytics.track('bot_check_failed', { reason });
  } catch {
    /* analytics is best-effort */
  }
  return new Error(BOT_CHECK_USER_MESSAGE);
}

export async function withBotCheck<T>(call: (token: string) => Promise<T>): Promise<T> {
  let token: string;
  try {
    token = await getProof();
  } catch (e) {
    throw failed('solve');
  }
  try {
    return await call(token);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (!msg.includes(BOT_CHECK_PREFIX)) throw err; // unrelated failure — bubble up

    let fresh: string;
    try {
      // Force a fresh solve rather than another pooled proof: the first
      // rejection is almost always staleness, and pooled proofs expire as a
      // batch, so the retry must not draw from the same stale pool. During a
      // traffic surge the server asks for a harder proof (`[POW_BITS:n]`).
      const harder = /\[POW_BITS:(\d{2})\]/.exec(msg);
      const bits = harder ? Math.min(28, Number(harder[1])) : 0;
      fresh = bits > POW_DIFFICULTY_BITS ? await solveChunked(bits) : await solveOne();
    } catch (e) {
      throw failed('retry');
    }
    try {
      return await call(fresh);
    } catch (err2) {
      const msg2 = err2 instanceof Error ? err2.message : String(err2);
      if (msg2.includes(BOT_CHECK_PREFIX)) throw failed('rejected');
      throw err2;
    }
  }
}
